import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import './Global.css';

const ReportsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    type: 'STANDARD',
    format: 'XLSX',
    periodStart: '',
    periodEnd: '',
    detailed: false,
    formatoSimples: false,
    mapaHorasAula: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);

    try {
      const payload = {
        type: formData.type,
        format: formData.format,
        detailed: formData.detailed,
        formatoSimples: formData.formatoSimples,
        mapaHorasAula: formData.mapaHorasAula,
      };

      if (formData.type === 'CUSTOM') {
        if (!formData.periodStart || !formData.periodEnd) {
          toast.error('Para relatórios customizados, informe o período');
          setGenerating(false);
          return;
        }
        payload.periodStart = formData.periodStart;
        payload.periodEnd = formData.periodEnd;
      }

      const response = await api.post('/reports/generate', payload);
      // O Mapa de Horas-Aula sai em dois arquivos (efetivo do colégio e
      // demais); o backend devolve o segundo em `adicionais`.
      const adicionais = response.data.adicionais || [];
      toast.success(
        adicionais.length > 0
          ? `${adicionais.length + 1} relatórios gerados: efetivo do CMCB e demais militares`
          : 'Relatório gerado com sucesso!',
      );

      if (response.data.id) {
        await downloadReport(response.data.id);
      }
      for (const extra of adicionais) {
        if (extra?.id) await downloadReport(extra.id);
      }

      setFormData({
        type: 'STANDARD',
        format: 'XLSX',
        periodStart: '',
        periodEnd: '',
        detailed: false,
        formatoSimples: false,
        mapaHorasAula: false,
      });
      loadData();
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório: ' + (error.response?.data?.message || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = async (reportId) => {
    try {
      const response = await api.get(`/reports/${reportId}/download`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      let fileName = `relatorio_${dateStr}`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success('Download iniciado!');
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      toast.error('Erro ao baixar relatório: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando...</p>
      </div>
    </>
  );

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container">
        <div className="page-header-new">
          <div>
            <h1 style={{ margin: 0 }}>Relatórios</h1>
            <p className="subtitle">Gere relatórios de horas dos militares</p>
          </div>
        </div>

        <div className="section">
          <h2 className="section-title">Gerar Novo Relatório</h2>
          <form onSubmit={handleGenerate} className="form" style={{ padding: 0, paddingTop: '16px' }}>
            <div className="form-row">
              <div className="form-group">
                <label>Tipo de Relatório</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="STANDARD">Padrão (16 mês anterior - 15 mês atual)</option>
                  <option value="CUSTOM">Personalizado</option>
                </select>
              </div>

              <div className="form-group">
                <label>Formato</label>
                <select
                  value={formData.format}
                  onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                >
                  <option value="XLSX">Excel (.xlsx)</option>
                  <option value="PDF">PDF (.pdf)</option>
                  <option value="CSV">CSV (.csv)</option>
                </select>
              </div>
            </div>

            {formData.type === 'CUSTOM' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Data Inicial</label>
                  <input
                    type="date"
                    value={formData.periodStart}
                    onChange={(e) => setFormData({ ...formData, periodStart: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Data Final</label>
                  <input
                    type="date"
                    value={formData.periodEnd}
                    onChange={(e) => setFormData({ ...formData, periodEnd: e.target.value })}
                  />
                </div>
              </div>
            )}

            <div
              className="form-group"
              style={{
                background: formData.detailed ? 'var(--primary-bg)' : '#f9fafb',
                border: `1px solid ${formData.detailed ? 'var(--primary)' : '#e5e7eb'}`,
                borderRadius: '8px',
                padding: '14px 16px',
                marginTop: '8px',
                transition: 'all 0.2s',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: 'pointer',
                  marginBottom: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.detailed}
                  onChange={(e) => setFormData({ ...formData, detailed: e.target.checked })}
                  style={{ marginTop: '3px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>
                  <strong style={{ display: 'block', marginBottom: '2px' }}>
                    Relatório Detalhado
                  </strong>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Inclui uma aba adicional mostrando exatamente quais dias cada militar foi registrado,
                    com horários de entrada/saída e observações.
                  </span>
                </span>
              </label>
            </div>

            <div
              className="form-group"
              style={{
                background: formData.formatoSimples ? 'var(--primary-bg)' : '#f9fafb',
                border: `1px solid ${formData.formatoSimples ? 'var(--primary)' : '#e5e7eb'}`,
                borderRadius: '8px',
                padding: '14px 16px',
                marginTop: '8px',
                transition: 'all 0.2s',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: 'pointer',
                  marginBottom: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.formatoSimples}
                  onChange={(e) => setFormData({ ...formData, formatoSimples: e.target.checked })}
                  style={{ marginTop: '3px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>
                  <strong style={{ display: 'block', marginBottom: '2px' }}>
                    Formato SMAEI
                  </strong>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Exporta uma planilha enxuta com uma linha por turno e colunas
                    MATRICULA, DATA, ENTRADA, SAIDA e CARGA_HORARIA_HORAS — pronta
                    para importação em outros sistemas. Disponível para Excel e CSV.
                    <strong style={{ display: 'block', marginTop: '4px', color: '#5B21B6' }}>
                      Civis não entram neste formato: a planilha casa as linhas por
                      matrícula funcional, que civil não possui.
                    </strong>
                  </span>
                </span>
              </label>
            </div>

            <div style={{ marginTop: '12px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: 'pointer',
                  marginBottom: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.mapaHorasAula}
                  onChange={(e) =>
                    setFormData({ ...formData, mapaHorasAula: e.target.checked })
                  }
                  style={{ marginTop: '3px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>
                  <strong style={{ display: 'block', marginBottom: '2px' }}>
                    Mapa de Horas-Aula (Portaria 676/2026)
                  </strong>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Modelo do Anexo I da Portaria nº 676/2026-CMDO/CBMCE: posto com quadro,
                    matrícula, nome, atividade de ensino e/ou instrução exercida no ciclo
                    (art. 5º) e total de horas-aula, com a relação das portarias de
                    nomeação e assinatura do comandante da unidade. Sai em{' '}
                    <strong>dois PDFs</strong>: um do efetivo do CMCB e outro dos demais
                    militares. Só para PDF. Desmarcado, sai o QTM de sempre.
                    <strong style={{ display: 'block', marginTop: '4px', color: '#5B21B6' }}>
                      Este modelo é só de militares — civis não entram.
                    </strong>
                  </span>
                </span>
              </label>
            </div>

            <div
              style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                background: '#f9fafb',
                borderRadius: '6px',
                padding: '10px 12px',
                marginTop: '4px',
              }}
            >
              <strong>Informações:</strong> As horas são arredondadas para a hora cheia superior
              (ex.: 2h10min → 3h). Militares sem registros no período não são incluídos no relatório.
            </div>

            <button type="submit" className="btn-primary" disabled={generating}>
              {generating ? 'Gerando...' : 'Gerar Relatório'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ReportsManagement;
